import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Select, Switch, Spin, Button, Tooltip } from 'antd';
import { PanelLeftClose, PanelLeft, Package } from 'lucide-react';
import { useMultiStrike } from './useMultiStrike';
import { MULTI_STRIKE_PERIODS, getModeLabel } from './multi-strike-utils';
import { RESOLUTIONS } from '@/utils/helper';
import StrikeSelector from './StrikeSelector';
import MultiStrikeChart from './MultiStrikeChart';
import FeedStatus from '@/components/ui/FeedStatus/FeedStatus';
import './index.scss';

const MultiStrikes = () => {
  const { mode: urlMode } = useParams();
  const mode = urlMode || 'lp';

  const {
    instruments,
    selectedInstrument,
    expiryOptions,
    selectedExpiry,
    strikeCount,
    selectionPreset,
    resolution,
    period,
    showCombined,
    showAtp,
    showIndividual,
    oiView,
    showStrikePanel,
    isLoading,
    isDark,
    isLiveFeedActive,
    isMarketOpen,
    atmStrike,
    strikeTokenMap,
    allStrikes,
    selectedStrikes,
    strikeTableData,
    criticalPoints,
    chartData,
    chartLoading,
    onChangeInstrument,
    onChangeExpiry,
    onChangeStrikeCount,
    onChangeSelectionPreset,
    onChangeResolution,
    onChangePeriod,
    onToggleStrike,
    onChangeCombined,
    onChangeAtp,
    onChangeIndividual,
    onChangeOiView,
    onToggleStrikePanel,
  } = useMultiStrike(mode);

  const instrumentOptions = useMemo(
    () => instruments.map((i) => ({ value: i, label: i })),
    [instruments],
  );
  const expiryDateOptions = useMemo(
    () => expiryOptions.map((e) => ({ value: e, label: e })),
    [expiryOptions],
  );
  const resolutionOptions = useMemo(
    () => RESOLUTIONS.map((r) => ({ value: r, label: r })),
    [],
  );
  const periodOptions = useMemo(
    () => MULTI_STRIKE_PERIODS.map((p) => ({ value: p, label: p })),
    [],
  );

  return (
    <div className="ms-page">
      {/* Controls bar */}
      <div className="config-container">
        {instruments.length > 0 && (
          <div className="config-group">
            <label>Instrument</label>
            <Select
              showSearch
              options={instrumentOptions}
              style={{ width: 150 }}
              onChange={onChangeInstrument}
              value={selectedInstrument}
              size="middle"
            />
          </div>
        )}

        {expiryOptions.length > 0 && (
          <div className="config-group">
            <label>Expiry</label>
            <Select
              options={expiryDateOptions}
              style={{ width: 130 }}
              onChange={onChangeExpiry}
              value={selectedExpiry}
              size="middle"
            />
          </div>
        )}

        <div className="config-group">
          <label>Resolution</label>
          <Select
            options={resolutionOptions}
            style={{ width: 80 }}
            onChange={onChangeResolution}
            value={resolution}
            size="middle"
          />
        </div>

        <div className="config-group">
          <label>Period</label>
          <Select
            options={periodOptions}
            style={{ width: 70 }}
            onChange={onChangePeriod}
            value={period}
            size="middle"
          />
        </div>

        {mode === 'lp' && (
          <>
            <div className="config-group">
              <label>Combined</label>
              <Switch
                checked={showCombined}
                onChange={onChangeCombined}
                size="small"
              />
            </div>
            {showCombined && (
              <div className="config-group">
                <label>ATP</label>
                <Switch
                  checked={showAtp}
                  onChange={onChangeAtp}
                  size="small"
                />
              </div>
            )}
            <div className="config-group">
              <label>Individual</label>
              <Switch
                checked={showIndividual}
                onChange={onChangeIndividual}
                size="small"
              />
            </div>
          </>
        )}

        <div className="config-group">
          <label>Strike Panel</label>
          <Tooltip title={showStrikePanel ? 'Hide strike panel' : 'Show strike panel'}>
            <Button
              icon={showStrikePanel ? <PanelLeftClose size={18} /> : <PanelLeft size={18} />}
              onClick={onToggleStrikePanel}
              className='ms-panel-toggle'
            />
          </Tooltip>
        </div>

        <FeedStatus isMarketOpen={isMarketOpen} isLiveFeedActive={isLiveFeedActive} />
      </div>

      {/* Main content area */}
      <div className="ms-content">
        {isLoading ? (
          <div className="ms-loader">
            <Spin size="large" />
          </div>
        ) : instruments.length > 0 ? (
          <>
            {/* Strike selector panel (left) */}
            {showStrikePanel && (
              <div className="ms-panel">
                <StrikeSelector
                  mode={mode}
                  allStrikes={allStrikes}
                  selectedStrikes={selectedStrikes}
                  strikeTableData={strikeTableData}
                  criticalPoints={criticalPoints}
                  atmStrike={atmStrike}
                  strikeCount={strikeCount}
                  selectionPreset={selectionPreset}
                  onChangeStrikeCount={onChangeStrikeCount}
                  onChangeSelectionPreset={onChangeSelectionPreset}
                  onToggleStrike={onToggleStrike}
                />
              </div>
            )}

            {/* Chart area (right) */}
            <div className="ms-chart-area">
              {chartData || chartLoading ? (
                <MultiStrikeChart
                  mode={mode}
                  chartData={chartData}
                  chartLoading={chartLoading}
                  isDark={isDark}
                  oiView={oiView}
                  showIndividual={showIndividual}
                  showCombined={showCombined}
                  showAtp={showAtp}
                  onChangeOiView={onChangeOiView}
                  strikeTokenMap={strikeTokenMap}
                  selectedStrikes={selectedStrikes}
                  isLiveFeedActive={isLiveFeedActive}
                  isMarketOpen={isMarketOpen}
                  resolution={resolution}
                />
              ) : (
                <div className="ms-empty">
                  <Package size={48} strokeWidth={1} />
                  <span>Select strikes to view chart data</span>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="ms-empty">
            <Package size={48} strokeWidth={1} />
            <span>Loading instruments…</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiStrikes;
